import { Trans } from '@lingui/react/macro';
import SimpleBar from 'simplebar-react';

import { Endpoints } from '@/api';
import { Comment } from '@/components/comments/Comment/Comment';
import { NewComment } from '@/components/comments/NewComment/NewComment';
import Paragraph from '@/design_system/Typography/Paragraph';
import { hasPermission } from '@/models/user';
import { useCurrentSession } from '@/services/auth';
import useViewPort from '@/utils/useViewport';

export type IComment = (
  | Endpoints['GET /requests/:id/comments']
  | Endpoints['GET /requests/:id/articles/:articleId/comments']
  | Endpoints['GET /shipments/:id/comments']
)['response']['comments'][number];

interface Props {
  comments: IComment[];
  displayArticleName?: boolean;
  isReadOnly?: (comment: IComment) => boolean;
  isCreatingComment?: boolean;
  onCommentCreated: (data: {
    content: string;
    visibility: IComment['visibility'];
    entityId: string | null;
  }) => Promise<void>;
  onCommentUpdated: (data: { commentId: string; content: string }) => Promise<void>;
  onCommentDeleted: (data: { commentId: string }) => Promise<void>;
  legacyLayout?: boolean;
}

export const Comments = ({
  comments,
  displayArticleName,
  isReadOnly,
  isCreatingComment,
  onCommentCreated,
  onCommentUpdated,
  onCommentDeleted,
  legacyLayout,
}: Props) => {
  const { isMobile } = useViewPort();
  const { currentSession } = useCurrentSession();

  return (
    <>
      <div
        style={{
          padding: !legacyLayout
            ? isMobile
              ? '0.5rem 0'
              : '1rem'
            : isMobile
              ? '1px 2px'
              : '0 32px 8px 32px',
        }}
      >
        <NewComment
          creator={currentSession}
          onCommentCreated={onCommentCreated}
          isLoading={isCreatingComment}
        />
      </div>

      <SimpleBar
        style={{
          padding: isMobile ? undefined : !legacyLayout ? '0 1rem 1.5rem 1rem' : '0 32px',
          overflow: 'auto',
          maxHeight: isMobile ? undefined : '30rem',
        }}
        autoHide={false}
      >
        {comments.length === 0 && (
          <Paragraph
            size="200"
            color="secondary"
            // eslint-disable-next-line lingui/no-unlocalized-strings
            style={{ margin: !legacyLayout ? (isMobile ? '0 0.25rem' : '0 3.5rem') : '16px 0' }}
          >
            <Trans id="comments.no-comment">No comments yet</Trans>
          </Paragraph>
        )}

        {comments
          .sort((c1, c2) => new Date(c2.createdAt).getTime() - new Date(c1.createdAt).getTime())
          .map((comment) => {
            const canModerateCommentOrgComment =
              (comment.visibility === 'organization' || comment.visibility === 'public') &&
              hasPermission(currentSession, 'moderate_comment', {
                organizationId: comment.organizationId,
              });

            const canModerateEntityComment =
              comment.visibility === 'entity' &&
              hasPermission(currentSession, 'moderate_comment', {
                entityId: comment.entityId,
              });

            return (
              <Comment
                key={comment.id}
                comment={comment}
                displayArticleName={!!displayArticleName}
                isReadOnly={
                  (comment.creatorId !== currentSession?.id &&
                    !canModerateCommentOrgComment &&
                    !canModerateEntityComment) ||
                  (isReadOnly && isReadOnly(comment))
                }
                onCommentUpdated={async (updatedContent) => {
                  await onCommentUpdated({ commentId: comment.id, content: updatedContent });
                }}
                onCommentDeleted={async () => {
                  await onCommentDeleted({ commentId: comment.id });
                }}
              />
            );
          })}
      </SimpleBar>
    </>
  );
};
